'use client';

import type { ComponentPropsWithoutRef, ElementRef } from 'react';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { forwardRef } from 'react';

import { cn } from '@/libs/tailwind';

const Accordion = AccordionPrimitive.Root;

const AccordionItem = forwardRef<
  ElementRef<typeof AccordionPrimitive.Item>,
  ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Item
    ref={ref}
    className={cn(className, 'border-b border-[#ECEEF6] w-full overflow-hidden')}
    {...props}
  />
));
AccordionItem.displayName = 'AccordionItem';

const AccordionTrigger = forwardRef<
  ElementRef<typeof AccordionPrimitive.Trigger>,
  ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Header>
    <AccordionPrimitive.Trigger
      ref={ref}
      className={cn(
        'w-full group',
        'hover:bg-gray-50/50 cursor-pointer',
        'flex flex-col',
        'self-stretch',
        className,
      )}
      style={{ padding: '20px 24px 20px 32px' }}
      {...props}
    >
      <div className="flex items-center justify-between w-full text-[16px] font-medium text-[#000]">
        <div className="text-left pr-8 flex-1 min-w-0 overflow-hidden text-ellipsis">
          {Array.isArray(children) ? children[0] : children}
        </div>
        <svg
          width="28"
          height="28"
          viewBox="0 0 28 28"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className={cn(
            'flex-shrink-0 transition-transform duration-200 ease-[cubic-bezier(0.87,0,0.13,1)]',
            'group-data-[state=open]:rotate-180',
          )}
        >
          <path
            d="M14.0002 18.3752C13.7785 18.3752 13.5568 18.2935 13.3818 18.1185L7.25684 11.9935L8.4935 10.7568L14.0002 16.2635L19.5068 10.7568L20.7435 11.9935L14.6185 18.1185C14.4435 18.2935 14.2218 18.3752 14.0002 18.3752Z"
            fill="#323346"
          />
        </svg>
      </div>
      {Array.isArray(children) && children.slice(1)}
    </AccordionPrimitive.Trigger>
  </AccordionPrimitive.Header>
));
AccordionTrigger.displayName = 'AccordionTrigger';

const AccordionContent = forwardRef<
  ElementRef<typeof AccordionPrimitive.Content>,
  ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    className={cn(
      className,
      'text-[17px] leading-[140%] tracking-[-0.34px] text-[#000] w-full text-left',
      'overflow-hidden transition-all duration-300 ease-[cubic-bezier(0.87,0,0.13,1)]',
      'data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down',
      'data-[state=closed]:opacity-0 data-[state=open]:opacity-100',
    )}
    {...props}
  >
    <div className="text-left">{children}</div>
  </AccordionPrimitive.Content>
));
AccordionContent.displayName = 'AccordionContent';

export { Accordion, AccordionContent, AccordionItem, AccordionTrigger };
